// core/services/regionsService.ts — the canonical region list, fetched once.
//
// The list lives on the server (backend/apps/accounts/regions.py) and is served
// from GET /users/regions/ so web, mobile and the API can never disagree about
// what a valid region is. Cached for the session: it changes about as often as
// the map does, and re-fetching it on every Settings visit would be silly.

import { api } from '@/core/lib/apiClient';

export interface RegionOption {
  /** ISO 3166-1 alpha-2. */
  code: string;
  name: string;
}

export interface RegionCatalogue {
  regions: RegionOption[];
  /** Server-enforced cap on how many interests one member may pick. */
  maxInterests: number;
}

let cached: RegionCatalogue | null = null;
let inFlight: Promise<RegionCatalogue> | null = null;

export const regionsService = {
  async list(): Promise<RegionCatalogue> {
    if (cached) return cached;
    // Share one request between concurrent callers — the Settings section and a
    // profile row can both ask on the same tick.
    inFlight ??= api<RegionCatalogue>('/users/regions/').then((r) => {
      cached = r;
      inFlight = null;
      return r;
    });
    return inFlight;
  },

  /** Display name for a code, or the code itself if it is not in the list. */
  nameFor(code: string): string {
    return cached?.regions.find((r) => r.code === code)?.name ?? code;
  },

  /** Test seam — resets the session cache. */
  _reset(): void {
    cached = null;
    inFlight = null;
  },
};
